import type { Effort, ModelId } from '../../../server/src/model-catalog';

/**
 * One WebSocket per open chat page. We don't multiplex multiple
 * sessions over one socket — the server keys runs by the socket's
 * authenticated identity, so opening another tab opens another
 * connection, which is fine.
 *
 * Auth: the browser sends the httpOnly `mc_token` cookie on the
 * upgrade request automatically (same-origin in prod, via Vite
 * proxy in dev). No token in the URL.
 *
 * Reconnect policy: exponential backoff up to 10 s. After a reconnect,
 * queued actions go first, then a fresh subscribe asks for current run
 * state. Already-sent actions are never replayed.
 */

import { api } from './api';
import type { PermissionMode } from './settings';

export type ToolCall = {
  id: string;
  name: string;
  input: unknown;
  result?: string;
  isError?: boolean;
};

/**
 * One ordered slot in an assistant turn. `text` blocks own a
 * fragment of model-generated prose; `tool_use` blocks reference a
 * `ToolCall` in the same message via `toolCallId`. The list order
 * is the canonical order the model produced — text-then-tool-then-
 * more-text is faithful, no longer "all tools first, all text last"
 * as before.
 */
export type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; toolCallId: string }
  /** A context-compaction boundary — renders as a "context compacted"
   * divider in the transcript. Positional, so it lives inline in the
   * ordered block list. Mirrors the server `ContentBlock`. */
  | { type: 'compact'; trigger: 'auto' | 'manual'; preTokens?: number; postTokens?: number }
  /** A safety-refusal model swap — renders as a "switched to Opus"
   * divider. Positional, mirrors the server `ContentBlock`. */
  | { type: 'model_fallback'; fromModel: string; toModel: string };

export type UsageInfo = {
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
  totalTokens: number;
  /** Current context occupancy from the provider's last request, when available. */
  contextTokens?: number | null;
  contextWindow: number | null;
  costUsd: number | null;
  model: string | null;
};

export type RateLimitInfo = {
  status: string;
  resetsAt: number | null;
  rateLimitType: string;
  overageStatus: string | null;
  isUsingOverage: boolean;
  /** Per-profile output-token cap in the current window. `null`
   * means no custom cap is configured. */
  userCap: number | null;
  /** Output tokens this profile has spent in the current window. */
  userUsed: number;
  /** 0..100 quota consumed. `null` when there's no custom cap and
   * the shared pool is still `allowed` (nothing meaningful to
   * show). Forced to 100 once the shared pool is exhausted. */
  userPercent: number | null;
};

export type ServerEvent =
  | {
      kind: 'user_message';
      messageId: string;
      text: string;
      clientRequestId?: string;
      attachments?: string[];
    }
  | { kind: 'assistant_start'; messageId: string; startedAtMs: number }
  | { kind: 'thinking_delta'; messageId: string; text: string }
  | {
      kind: 'assistant_delta';
      messageId: string;
      text: string;
      /** Index of the content block this delta belongs to. New
       * field — older servers omit it; the client treats it as
       * "append to a tail text block" in that case. */
      blockIndex?: number;
    }
  | {
      kind: 'tool_use_start';
      messageId: string;
      toolUseId: string;
      name: string;
      input: unknown;
      /** Where this tool call sits in the message's ordered content
       * blocks (same coordinate system as `assistant_delta`). Older
       * servers omit it; the client appends to the tail. */
      blockIndex?: number;
    }
  | {
      kind: 'tool_use_end';
      messageId: string;
      toolUseId: string;
      result: string;
      isError: boolean;
    }
  | {
      /** Server saw the CLI auto-compact the context mid-turn. The
       * client inserts a compact block at `blockIndex` (same
       * coordinate system as `assistant_delta`/`tool_use_start`). */
      kind: 'compact_boundary';
      messageId: string;
      blockIndex: number;
      trigger: 'auto' | 'manual';
      preTokens?: number;
      postTokens?: number;
    }
  | {
      /** Transient compaction lifecycle (a `/compact` run or an
       * auto-compaction). Drives a "compacting…" indicator and, on
       * `failed`, a clean inline notice — NOT a persisted block.
       * `done` precedes the success `compact_boundary`; `failed`
       * carries the CLI's error and is followed by no boundary. */
      kind: 'compact_status';
      messageId: string;
      phase: 'start' | 'done' | 'failed';
      error?: string;
    }
  | {
      /** Server saw the API refuse a Fable turn and the CLI retry it
       * on Opus. The client inserts a `model_fallback` block at
       * `blockIndex` and latches its model picker to `toModel` so the
       * next turn continues on Opus instead of snapping back to Fable
       * and tripping the same filter. */
      kind: 'model_fallback';
      messageId: string;
      blockIndex: number;
      fromModel: string;
      toModel: string;
    }
  | { kind: 'assistant_end'; messageId: string }
  | { kind: 'activity'; messageId: string; message: string }
  | { kind: 'usage'; usage: UsageInfo }
  | { kind: 'rate_limit'; rateLimit: RateLimitInfo }
  | { kind: 'idle'; requestId?: number }
  | {
      kind: 'done';
      durationMs: number;
      claudeSessionId?: string | null;
      usage?: UsageInfo | null;
      rateLimit?: RateLimitInfo | null;
    }
  | { kind: 'error'; message: string; clientRequestId?: string }
  | {
      kind: 'snapshot';
      requestId?: number;
      clientRequestId?: string;
      messageId: string;
      assistantText: string;
      thinkingText: string;
      toolCalls: ToolCall[];
      /** Ordered text/tool blocks. Optional for backwards-compat
       * with pre-fix servers — when missing the client synthesises
       * it from `assistantText` + `toolCalls` (loses ordering, but
       * matches the old behaviour). */
      contentBlocks?: ContentBlock[];
      isStreaming: boolean;
      elapsedMs: number;
      compacting?: boolean;
      compactError?: string | null;
      activityElapsedMs?: number | null;
      statusMessage?: string | null;
      error?: string | null;
      usage?: UsageInfo | null;
      rateLimit?: RateLimitInfo | null;
    };

export type ClientModel = ModelId;
export type ClientPermissionMode = PermissionMode;
export type ClientEffort = Effort;

export type ClientMsg =
  | { kind: 'subscribe'; sessionId: string }
  | {
      kind: 'send';
      clientRequestId?: string;
      sessionId: string;
      text: string;
      model?: ClientModel;
      permissionMode?: ClientPermissionMode;
      attachments?: string[];
      effort?: ClientEffort;
    }
  | {
      kind: 'edit_resend';
      clientRequestId?: string;
      sessionId: string;
      messageId: string;
      text: string;
      model?: ClientModel;
      permissionMode?: ClientPermissionMode;
      attachments?: string[];
      effort?: ClientEffort;
    }
  | { kind: 'abort'; sessionId: string };

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed';

type Listener = (ev: ServerEvent) => void;
type StateListener = (s: ConnectionState) => void;
type Submission = Extract<ClientMsg, { kind: 'send' | 'edit_resend' }>;
const SUBMIT_ACK_TIMEOUT_MS = 15_000;
const UNCONFIRMED_SUBMISSION = 'Подтверждение не получено. Проверьте историю перед повтором.';

// Every kind the server is allowed to emit. A frame whose `kind`
// isn't here is either a stale pod (new client vs old backend) or
// a bug; either way, drop it rather than hand an unknown shape
// to the switch and hit runtime crashes deep in a reducer.
const VALID_EVENT_KINDS = new Set<ServerEvent['kind']>([
  'user_message',
  'assistant_start',
  'thinking_delta',
  'assistant_delta',
  'tool_use_start',
  'tool_use_end',
  'compact_boundary',
  'compact_status',
  'model_fallback',
  'assistant_end',
  'activity',
  'usage',
  'rate_limit',
  'idle',
  'done',
  'error',
  'snapshot',
]);

function isServerEvent(v: unknown): v is ServerEvent {
  if (!v || typeof v !== 'object') return false;
  const kind = (v as { kind?: unknown }).kind;
  return typeof kind === 'string' && (VALID_EVENT_KINDS as Set<string>).has(kind);
}

function chatUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/chat`;
}

export class ChatSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private stateListeners = new Set<StateListener>();
  private state: ConnectionState = 'idle';
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private queued: Exclude<ClientMsg, { kind: 'subscribe' }>[] = [];
  private subscribedSessionId: string | null = null;
  private syncVersion = 0;
  private submissions = new Map<
    string,
    {
      resolve: () => void;
      reject: (error: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();

  connect(): void {
    if (this.disposed) return;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.setState('connecting');
    const ws = new WebSocket(chatUrl());
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.reconnectAttempts = 0;
      const rest = this.queued;
      this.queued = [];
      for (const q of rest) ws.send(JSON.stringify(q));
      this.subscribeNow(ws);
      this.setState('open');
    };
    ws.onmessage = (e) => {
      if (this.ws !== ws) return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(e.data));
      } catch {
        return;
      }
      if (!isServerEvent(parsed)) {
        // eslint-disable-next-line no-console
        console.warn('[ws] dropping unrecognised frame', parsed);
        return;
      }
      // A rejection belongs only to its still-pending submission. A late
      // response must not clear the hook's state for a newer command.
      if (
        parsed.kind === 'error' &&
        typeof parsed.clientRequestId === 'string' &&
        !this.submissions.has(parsed.clientRequestId)
      )
        return;
      if (
        (parsed.kind === 'user_message' || parsed.kind === 'snapshot' || parsed.kind === 'error') &&
        typeof parsed.clientRequestId === 'string'
      ) {
        this.settleSubmission(
          parsed.clientRequestId,
          parsed.kind === 'error' ? new Error(parsed.message) : undefined,
        );
      }
      if (
        (parsed.kind === 'idle' || parsed.kind === 'snapshot') &&
        (parsed.requestId !== undefined || parsed.kind === 'idle') &&
        parsed.requestId !== this.syncVersion
      ) {
        return;
      }
      for (const fn of this.listeners) fn(parsed);
    };
    ws.onerror = () => {
      // onclose follows; handle reconnect there
    };
    ws.onclose = () => {
      // A stale socket's close must not null a newer live connection.
      if (this.ws !== ws) return;
      const handshakeFailed = this.state === 'connecting';
      this.ws = null;
      this.setState('closed');
      if (this.disposed) return;
      // Upgrade 401 never surfaces as HTTP to JS — probe /me so
      // AuthProvider can drop the session instead of reconnecting forever.
      if (handshakeFailed) {
        void api('/auth/me').catch(() => {
          /* 401 already notified AuthProvider */
        });
      }
      this.scheduleReconnect();
    };
  }

  send(msg: ClientMsg): void {
    if (this.disposed) return;
    if (msg.kind === 'abort') {
      for (const queued of this.queued) {
        if (
          queued.kind !== 'abort' &&
          queued.sessionId === msg.sessionId &&
          queued.clientRequestId
        ) {
          this.settleSubmission(
            queued.clientRequestId,
            new Error('Отправка отменена. Черновик сохранён.'),
          );
        }
      }
      this.queued = this.queued.filter(
        (queued) => queued.kind === 'abort' || queued.sessionId !== msg.sessionId,
      );
    }
    if (msg.kind === 'subscribe') {
      this.subscribedSessionId = msg.sessionId;
      this.syncVersion += 1;
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.subscribeNow(this.ws);
      } else {
        this.connect();
      }
      return;
    }
    // A pending subscribe reply predating a new action cannot describe
    // the run that action is about to create or abort.
    this.syncVersion += 1;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      // If the action is rejected (for example, another run is already
      // active), a fresh snapshot still needs to replace the stale ack.
      this.subscribeNow(this.ws);
    } else {
      this.queued.push(msg);
      this.connect();
    }
  }

  /** Resolve only when the server confirms this exact command; never replay an uncertain send. */
  submit(msg: Submission): Promise<void> {
    if (this.disposed) return Promise.reject(new Error('Соединение закрыто. Черновик сохранён.'));
    const clientRequestId = crypto.randomUUID();
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.settleSubmission(clientRequestId, new Error(UNCONFIRMED_SUBMISSION));
      }, SUBMIT_ACK_TIMEOUT_MS);
      this.submissions.set(clientRequestId, { resolve, reject, timer });
      try {
        this.send({ ...msg, clientRequestId });
      } catch (error) {
        this.settleSubmission(
          clientRequestId,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    });
  }

  /** Re-check after a background tab resumes, without replaying sent actions. */
  resume(): void {
    if (this.disposed) return;
    const ws = this.ws;
    if (ws?.readyState === WebSocket.CONNECTING) return;
    if (ws?.readyState === WebSocket.OPEN) {
      if (ws.bufferedAmount > 0) {
        this.subscribeNow(ws);
        return;
      }
      // Clear identity before close: late callbacks from this socket
      // must not close or mutate the replacement connection.
      this.ws = null;
      ws.close();
    }
    this.connect();
  }

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onState(fn: StateListener): () => void {
    this.stateListeners.add(fn);
    fn(this.state);
    return () => this.stateListeners.delete(fn);
  }

  dispose(): void {
    this.disposed = true;
    for (const id of this.submissions.keys()) {
      this.settleSubmission(id, new Error('Соединение закрыто. Черновик сохранён.'));
    }
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.ws?.close();
    this.ws = null;
    this.queued = [];
    this.subscribedSessionId = null;
    this.listeners.clear();
    this.stateListeners.clear();
  }

  private setState(s: ConnectionState): void {
    this.state = s;
    for (const fn of this.stateListeners) fn(s);
  }

  private settleSubmission(clientRequestId: string, error?: Error): void {
    const pending = this.submissions.get(clientRequestId);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.submissions.delete(clientRequestId);
    // Only unsent commands remain queued; an already-sent command is never requeued.
    this.queued = this.queued.filter(
      (message) => message.kind === 'abort' || message.clientRequestId !== clientRequestId,
    );
    if (error) pending.reject(error);
    else pending.resolve();
  }

  private subscribeNow(ws: WebSocket): void {
    if (!this.subscribedSessionId) return;
    this.syncVersion += 1;
    ws.send(
      JSON.stringify({
        kind: 'subscribe',
        sessionId: this.subscribedSessionId,
        requestId: this.syncVersion,
      }),
    );
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const delay = Math.min(10_000, 500 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
